import { useState, useRef } from "react";

interface Props {
  productName: string;
  productId: string;
}

const MAX_BODY = 500;

export default function ReviewSubmitForm({ productName, productId }: Props) {
  const [open, setOpen] = useState(false);
  const [hoveredStar, setHoveredStar] = useState(0);
  const [rating, setRating] = useState(0);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [name, setName] = useState("");
  const [location, setLocation] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const formRef = useRef<HTMLFormElement>(null);

  const starLabels = ["", "Poor", "Fair", "Good", "Great", "Excellent"];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (rating === 0) {
      setError("Please select a star rating before submitting.");
      return;
    }
    if (body.length > MAX_BODY) {
      setError(`Review must be ${MAX_BODY} characters or fewer.`);
      return;
    }
    if (!name.trim()) {
      setError("Please enter your name.");
      return;
    }

    setSubmitting(true);

    const params = new URLSearchParams({
      rating: String(rating),
      title,
      review: body,
      name,
      location,
      email,
      product: productName,
      productId,
    });

    try {
      await fetch("https://readdy.ai/api/form/d768qvkbmgf2o8mm73c0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      setSubmitted(true);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 text-center">
        <div className="w-12 h-12 flex items-center justify-center bg-emerald-100 rounded-full mx-auto mb-3">
          <i className="ri-check-line text-2xl text-emerald-600"></i>
        </div>
        <h4 className="font-bold text-emerald-800 text-base mb-1">Thank you for your review!</h4>
        <p className="text-sm text-emerald-600">Your feedback helps other shoppers make confident decisions.</p>
        <button
          onClick={() => {
            setSubmitted(false);
            setOpen(false);
            setRating(0);
            setTitle("");
            setBody("");
            setName("");
            setLocation("");
            setEmail("");
          }}
          className="mt-4 text-xs font-semibold text-emerald-700 underline underline-offset-2 cursor-pointer"
        >
          Write another review
        </button>
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      {/* Toggle header */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-4 bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer"
      >
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-lg">
            <i className="ri-edit-line text-amber-600 text-base"></i>
          </div>
          <span className="font-bold text-gray-900 text-sm">Write a Review</span>
        </div>
        <div className="w-5 h-5 flex items-center justify-center text-gray-400">
          <i className={`${open ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-lg`}></i>
        </div>
      </button>

      {/* Form body */}
      {open && (
        <form
          ref={formRef}
          id="product-review-form"
          data-readdy-form
          onSubmit={handleSubmit}
          className="px-5 py-6 space-y-5 bg-white"
        >
          {/* Star picker */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">
              Your Rating <span className="text-red-400">*</span>
            </label>
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setRating(s)}
                  onMouseEnter={() => setHoveredStar(s)}
                  onMouseLeave={() => setHoveredStar(0)}
                  className="w-9 h-9 flex items-center justify-center cursor-pointer transition-transform hover:scale-110"
                  aria-label={`${s} stars`}
                >
                  <i
                    className={`text-2xl transition-colors ${
                      s <= (hoveredStar || rating)
                        ? "ri-star-fill text-amber-400"
                        : "ri-star-line text-gray-300"
                    }`}
                  ></i>
                </button>
              ))}
              {(hoveredStar || rating) > 0 && (
                <span className="ml-2 text-sm font-semibold text-amber-600">
                  {starLabels[hoveredStar || rating]}
                </span>
              )}
            </div>
          </div>

          {/* Review title */}
          <div>
            <label htmlFor="review-title" className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
              Review Title
            </label>
            <input
              id="review-title"
              name="title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Sum it up in one line..."
              maxLength={120}
              className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition"
            />
          </div>

          {/* Review body */}
          <div>
            <label htmlFor="review-body" className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
              Your Review <span className="text-red-400">*</span>
            </label>
            <textarea
              id="review-body"
              name="review"
              value={body}
              onChange={(e) => {
                if (e.target.value.length <= MAX_BODY) setBody(e.target.value);
              }}
              placeholder={`Share your experience with ${productName}...`}
              rows={4}
              required
              className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition resize-none"
            />
            <p className={`text-xs mt-1 text-right ${body.length >= MAX_BODY ? "text-red-500 font-semibold" : "text-gray-400"}`}>
              {body.length}/{MAX_BODY}
            </p>
          </div>

          {/* Name + Location row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="review-name" className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                Your Name <span className="text-red-400">*</span>
              </label>
              <input
                id="review-name"
                name="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. John D."
                required
                className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition"
              />
            </div>
            <div>
              <label htmlFor="review-location" className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                Location <span className="text-gray-300 font-normal">(optional)</span>
              </label>
              <input
                id="review-location"
                name="location"
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="e.g. Austin, TX"
                className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition"
              />
            </div>
          </div>

          {/* Email */}
          <div>
            <label htmlFor="review-email" className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
              Email <span className="text-gray-300 font-normal">(optional — never shown publicly)</span>
            </label>
            <input
              id="review-email"
              name="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition"
            />
          </div>

          {/* Hidden fields */}
          <input type="hidden" name="product" value={productName} />
          <input type="hidden" name="productId" value={productId} />

          {/* Error */}
          {error && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">
              <div className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5">
                <i className="ri-alert-line"></i>
              </div>
              <span>{error}</span>
            </div>
          )}

          {/* Submit */}
          <div className="flex items-center justify-between pt-1">
            <p className="text-xs text-gray-400">
              <span className="text-red-400">*</span> Required fields
            </p>
            <button
              type="submit"
              disabled={submitting || body.length > MAX_BODY}
              className="flex items-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:bg-gray-200 disabled:text-gray-400 text-white font-bold px-6 py-2.5 rounded-lg transition-all cursor-pointer whitespace-nowrap text-sm"
            >
              {submitting ? (
                <>
                  <div className="w-4 h-4 flex items-center justify-center animate-spin">
                    <i className="ri-loader-4-line"></i>
                  </div>
                  Submitting...
                </>
              ) : (
                <>
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-send-plane-fill"></i>
                  </div>
                  Submit Review
                </>
              )}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
